import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { Plus, Minus } from "lucide-react";
import * as urlService from "../view/urlService";
import { getFilters } from "../view/urlService";

const filters = [
    { id: 1, name: "Rule name", key: "rule", component: InputFilter },
    { id: 2, name: "Description", key: "details", component: InputFilter },
    { id: 3, name: "Severity", key: "severity", component: SeverityFilter },
    { id: 4, name: "Valid", key: "valid", component: ValidFilter },
    { id: 5, name: "Require Attention", key: "requireAttention", component: RequireAttentionFilter },
    { id: 6, name: "Notes", key: "note", component: InputFilter },
    { id: 7, name: "Compliance", key: "compliance", component: InputFilter },
    { id: 8, name: "Section", key: "sectionName", component: SectionDropdownFilter },
    { id: 9, name: "Subsection", key: "subsectionName", component: SubsectionDropdownFilter },
];

function getUniqueOptions(list, key, selectedValue) {
    const values = new Set();

    (list || []).forEach(item => {
        const value = item && item[key];
        if (value) {
            values.add(value);
        }
    });

    if (selectedValue) {
        values.add(selectedValue);
    }

    return Array.from(values)
        .sort((first, second) => first.localeCompare(second))
        .map(value => ({ value, label: value }));
}

function mergeOptions(currentOptions, nextOptions) {
    const values = new Set();

    [...(currentOptions || []), ...(nextOptions || [])].forEach(option => {
        if (option?.value) {
            values.add(option.value);
        }
    });

    return Array.from(values)
        .sort((first, second) => first.localeCompare(second))
        .map(value => ({ value, label: value }));
}

function ValidationResultTableSearchSection({ validationResultList }) {
    const history = useHistory();

    const [filterSections, setFilterSections] = useState([
        { selectedFilter: filters[0], value: '' }
    ]);
    const [dropdownOptions, setDropdownOptions] = useState({
        sectionName: [],
        subsectionName: []
    });

    useEffect(() => {
        const pageFilters = getFilters(true);
        const initialSections = [];

        filters.forEach((filter) => {
            if (pageFilters[filter.key]) {
                initialSections.push({
                    selectedFilter: filter,
                    value: pageFilters[filter.key]
                });
            }
        });

        if (initialSections.length) {
            setFilterSections(initialSections);
        }
    }, []);

    useEffect(() => {
        setDropdownOptions(prevOptions => ({
            sectionName: mergeOptions(
                prevOptions.sectionName,
                getUniqueOptions(validationResultList, 'sectionName')
            ),
            subsectionName: mergeOptions(
                prevOptions.subsectionName,
                getUniqueOptions(validationResultList, 'subsectionName')
            )
        }));
    }, [validationResultList]);

    const updateSearchParam = (key, value) => {
        if (value) {
            history.replace({
                pathname: history.location.pathname,
                search: urlService.addSearchFilters(key, value),
            });
        } else {
            history.replace({
                pathname: history.location.pathname,
                search: urlService.removeFilter(key),
            });
        }
    };

    const handleFilterChange = (index, selectedOption) => {
        const updatedSections = [...filterSections];
        const prevFilterKey = updatedSections[index].selectedFilter.key;
        const newFilter = filters.find((f) => f.id === selectedOption.value);

        // Check if the prevFilterKey is used by any other section
        const isPrevKeyUsedElsewhere = filterSections.some(
            (section, i) => i !== index && section.selectedFilter.key === prevFilterKey
        );

        // Remove previous filter from URL if not used elsewhere
        if (prevFilterKey && prevFilterKey !== newFilter.key && !isPrevKeyUsedElsewhere) {
            history.replace({
                pathname: history.location.pathname,
                search: urlService.removeFilter(prevFilterKey),
            });
        }

        updatedSections[index].selectedFilter = newFilter;
        updatedSections[index].value = '';
        setFilterSections(updatedSections);
    };

    const handleValueChange = (index, value) => {
        const updatedSections = [...filterSections];
        updatedSections[index].value = value;
        updateSearchParam(updatedSections[index].selectedFilter.key, value);
        setFilterSections(updatedSections);
    };

    const addFilterSection = () => {
        setFilterSections([
            ...filterSections,
            { selectedFilter: filters[0], value: '' },
        ]);
    };

    const removeFilterSection = (index) => {
        const section = filterSections[index];
        updateSearchParam(section.selectedFilter.key, '');
        const updated = filterSections.filter((_, i) => i !== index);
        setFilterSections(updated);
    };

    const renderFilterComponent = (filter, value, index) => {
        const Component = filter.component;
        return (
            <Component
                filterValue={value}
                onSearchChange={(val) => handleValueChange(index, val)}
                validationResultList={validationResultList}
                options={dropdownOptions[filter.key]}
            />
        );
    };

    // Convert filters to react-select options
    const filterOptions = filters.map(filter => ({
        value: filter.id,
        label: filter.name
    }));

    return (
        <div>
            {filterSections.map((section, index) => (
                <div key={index} className="oct_filter-row">
                    {/* Filter Type Dropdown */}
                    <div className="oct_filter-select-v2">
                        <Select
                            className="oct_selectsearch-v2"
                            classNamePrefix="oct-selectsearchsub-v2"
                            value={filterOptions.find(opt => opt.value === section.selectedFilter.id)}
                            onChange={(selectedOption) => handleFilterChange(index, selectedOption)}
                            options={filterOptions}
                            isSearchable={false}
                        />
                    </div>

                    {/* Input/Select Component */}
                    {renderFilterComponent(section.selectedFilter, section.value, index)}

                    {/* Add/Remove Buttons */}
                    <div className="oct_filter-actions-v2">
                        {index === filterSections.length - 1 && (
                            <button
                                className="oct_filter-btn-add"
                                onClick={addFilterSection}
                                title="Add Filter"
                            >
                                <Plus size={16} />
                            </button>
                        )}
                        {filterSections.length > 1 && (
                            <button
                                className="oct_filter-btn-remove"
                                onClick={() => removeFilterSection(index)}
                                title="Remove Filter"
                            >
                                <Minus size={16} />
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ValidFilter({ onSearchChange, filterValue }) {
    const options = [
        { value: '', label: 'Select' },
        { value: '1', label: 'Pass' },
        { value: '0', label: 'Fail' },
        { value: '-1', label: 'Unknown' }
    ];

    return (
        <div className="oct_filter-val-v2">
            <Select
                className="oct_selectsearch-v2"
                classNamePrefix="oct-selectsearchsub-v2"
                value={options.find(opt => opt.value === filterValue) || options[0]}
                onChange={(selectedOption) => onSearchChange(selectedOption.value)}
                options={options}
                isSearchable={false}
            />
        </div>
    );
}

function SeverityFilter({ onSearchChange, filterValue }) {
    const options = [
        { value: '', label: 'Select' },
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' }
    ];

    return (
        <div className="oct_filter-val-v2">
            <Select
                className="oct_selectsearch-v2"
                classNamePrefix="oct-selectsearchsub-v2"
                value={options.find(opt => opt.value === filterValue) || options[0]}
                onChange={(selectedOption) => onSearchChange(selectedOption.value)}
                options={options}
                isSearchable={false}
            />
        </div>
    );
}

function InputFilter({ onSearchChange, filterValue }) {
    return (
        <input
            className="oct_filter-input-v2"
            type="text"
            placeholder="Enter value..."
            value={filterValue}
            onChange={e => onSearchChange(e.target.value)}
        />
    );
}

function SectionDropdownFilter({ onSearchChange, filterValue, options }) {
    return (
        <DropdownValueFilter
            filterValue={filterValue}
            onSearchChange={onSearchChange}
            options={options}
        />
    );
}

function SubsectionDropdownFilter({ onSearchChange, filterValue, options }) {
    return (
        <DropdownValueFilter
            filterValue={filterValue}
            onSearchChange={onSearchChange}
            options={options}
        />
    );
}

function DropdownValueFilter({ onSearchChange, filterValue, options }) {
    const valueExists = (options || []).some(option => option.value === filterValue);
    const selectedOption = filterValue && !valueExists
        ? [{ value: filterValue, label: filterValue }]
        : [];
    const selectOptions = [{ value: '', label: 'Select' }, ...(options || []), ...selectedOption];

    return (
        <div className="oct_filter-val-v2">
            <Select
                className="oct_selectsearch-v2"
                classNamePrefix="oct-selectsearchsub-v2"
                value={selectOptions.find(opt => opt.value === filterValue) || selectOptions[0]}
                onChange={(selectedOption) => onSearchChange(selectedOption.value)}
                options={selectOptions}
                isSearchable={true}
            />
        </div>
    );
}

function RequireAttentionFilter({ onSearchChange, filterValue }) {
    const options = [
        { value: '', label: 'Select' },
        { value: '1', label: 'Yes' },
        { value: '0', label: 'No' }
    ];

    return (
        <div className="oct_filter-val-v2">
            <Select
                className="oct_selectsearch-v2"
                classNamePrefix="oct-selectsearchsub-v2"
                value={options.find(opt => opt.value === filterValue) || options[0]}
                onChange={(selectedOption) => onSearchChange(selectedOption.value)}
                options={options}
                isSearchable={false}
            />
        </div>
    );
}

function mapStateToProps(state) {
    const currentPath = window.location.pathname;
    let validationResultList = state.validatePage?.validationResultList;

    if (currentPath.includes('/analysis')) {
        validationResultList = state.analysisPage?.validationResultList;
    }

    if (currentPath.includes('/violations')) {
        validationResultList = state.violationsPage?.violations;
    }

    return {
        validationResultList: validationResultList || []
    };
}

export default connect(mapStateToProps)(ValidationResultTableSearchSection);
